"use client"

import { useCallback, useEffect, useState } from "react"
import { getMoneyContext, type MoneyContext } from "@/lib/money-api"
import { usePermissions } from "@/hooks/use-permissions"

export function useMoneyContext() {
  const { isLoaded, canAny } = usePermissions()
  const allowed = canAny([
    "money.accounts.use",
    "money.accounts.view",
    "money.accounts.manage",
    "money.sessions.manage",
  ])
  const [context, setContext] = useState<MoneyContext | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const refresh = useCallback(async () => {
    if (!allowed) return
    setLoading(true)
    setError(null)
    try {
      setContext(await getMoneyContext())
    } catch (cause) {
      setError(
        cause instanceof Error
          ? cause.message
          : "Could not load money settings",
      )
    } finally {
      setLoading(false)
    }
  }, [allowed])
  useEffect(() => {
    if (isLoaded && allowed) void Promise.resolve().then(refresh)
    else if (isLoaded) void Promise.resolve().then(() => setLoading(false))
  }, [isLoaded, allowed, refresh])
  return { context, loading: !isLoaded || loading, error, refresh, allowed }
}
